import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import {
  User, Organization, Trait, Question, Section, Exam,
  ExamAssignment, ExamResult, SystemSettings, Role, Status,
  AssessmentTemplate
} from "../types";

import {
  ORGS, USERS, TRAITS, QUESTIONS, SECTIONS, EXAMS, ASSIGNMENTS, RESULTS,
} from "../data/staticData";

// Firebase imports
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  where,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Group } from "services/groupService";

interface AppState {
  currentUser: User | null;
  orgs: Organization[];
  users: User[];
  traits: Trait[];
  questions: Question[];
  sections: Section[];
  exams: Exam[];
  assessment: AssessmentTemplate[];
  assignments: ExamAssignment[];
  results: ExamResult[];
  settings: SystemSettings;
  loading: boolean;
  groups: Group[];
  setGroups: React.Dispatch<React.SetStateAction<Group[]>>;

  setCurrentUser: (u: User | null) => void;
  setOrgs: React.Dispatch<React.SetStateAction<Organization[]>>;
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  setTraits: React.Dispatch<React.SetStateAction<Trait[]>>;
  setQuestions: React.Dispatch<React.SetStateAction<Question[]>>;
  setSections: React.Dispatch<React.SetStateAction<Section[]>>;
  setExams: React.Dispatch<React.SetStateAction<Exam[]>>;
  setAssignments: React.Dispatch<React.SetStateAction<ExamAssignment[]>>;
  setResults: React.Dispatch<React.SetStateAction<ExamResult[]>>;
  setSettings: React.Dispatch<React.SetStateAction<SystemSettings>>;

  // Firebase CRUD methods
  addOrg: (org: Omit<Organization, 'id'>) => Promise<void>;
  updateOrg: (id: string, data: Partial<Organization>) => Promise<void>;
  deleteOrg: (id: string) => Promise<void>;
  addUser: (user: Omit<User, 'id'>) => Promise<User>;
  updateUser: (id: string, data: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
}

const defaultSettings: SystemSettings = {
  siteName: "PsychoAssess",
  supportEmail: "support@psychoassess.com",
  maxOrgs: 100,
  defaultScale: 5,
  allowNegative: true,
  sessionTimeout: 30,
  maintenanceMode: false,
};

const AppContext = createContext<AppState>({} as AppState);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [traits, setTraits] = useState<Trait[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [assessment, setAssessment] = useState<AssessmentTemplate[]>([]);
  const [assignments, setAssignments] = useState<ExamAssignment[]>([]);
  const [results, setResults] = useState<ExamResult[]>([]);
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [groups, setGroups] = useState<Group[]>([]);

  // Helper function to convert Firestore Timestamp to string
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return new Date().toISOString();
    if (timestamp?.toDate) return timestamp.toDate().toISOString();
    if (typeof timestamp === "string") return timestamp;
    return new Date().toISOString();
  };

  // Fetch organizations from Firebase
  const fetchOrgs = async (): Promise<Organization[]> => {
    try {
      const q = query(collection(db, "organizations"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          domain: data.domain,
          primaryColor: data.primaryColor,
          plan: data.plan,
          status: data.status,
          adminCount: data.adminCount || 0,
          candidateCount: data.candidateCount || 0,
          createdAt: formatDate(data.createdAt),
          logo: data.logo,
        } as Organization;
      });
    } catch (error) {
      console.error("Error fetching orgs:", error);
      return [];
    }
  };

  // Fetch users from Firebase
  const fetchUsers = async (): Promise<User[]> => {
    try {
      const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          email: data.email,
          role: data.role,
          orgId: data.orgId,
          status: data.status,
          createdAt: formatDate(data.createdAt),
          hasPassword: data.hasPassword || false,
          authUid: data.authUid,
          phone: data.phone,
          avatar: data.avatar,
        } as User;
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      return [];
    }
  };

  const fetchGroups = async (): Promise<Group[]> => {
    try {
      const snapshot = await getDocs(collection(db, "groups"));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Group[];
    } catch (error) {
      console.error("Error fetching groups:", error);
      return [];
    }
  };

  const fetchExams = async (): Promise<Exam[]> => {
    try {
      const snapshot = await getDocs(collection(db, "exams"));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Exam[];
    } catch (error) {
      console.error("Error fetching exams:", error);
      return [];
    }
  };

  const fetchAssignments = async (): Promise<ExamAssignment[]> => {
    try {
      const snapshot = await getDocs(collection(db, "exam_assignments"));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ExamAssignment[];
    } catch (error) {
      console.error("Error fetching assignments:", error);
      return [];
    }
  };

  const fetchAssessments = async (): Promise<AssessmentTemplate[]> => {
    try {
      const snapshot = await getDocs(collection(db, "assessmentTemplates"));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AssessmentTemplate[];
    } catch (error) {
      console.error("Error fetching assessments:", error);
      return [];
    }
  };

  // Refresh all data
  const refreshData = async () => {
    setLoading(true);
    try {
      const [
        fetchedOrgs,
        fetchedUsers,
        fetchedAssessments,
        fetchedGroups,
        fetchedExams,
        fetchedAssignments
      ] = await Promise.all([
        fetchOrgs(),
        fetchUsers(),
        fetchAssessments(),
        fetchGroups(),
        fetchExams(),
        fetchAssignments()
      ]);

      setOrgs(fetchedOrgs);
      setUsers(fetchedUsers);
      setAssessment(fetchedAssessments);
      setGroups(fetchedGroups);
      setExams(fetchedExams);
      setAssignments(fetchedAssignments);

    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load logged-in user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('Raw user data from localStorage:', userData);

        // Map the user data to match your User type
        const mappedUser: User = {
          id: userData.userDocId || userData.uid || userData.id, // Use userDocId as id
          name: userData.name,
          email: userData.email,
          role: userData.role,
          orgId: userData.organizationId || userData.orgId,
          status: 'active',
          createdAt: new Date().toISOString(),
          hasPassword: true,
          authUid: userData.uid,
          phone: userData.phone,
          avatar: userData.avatar,
        };

        console.log('Mapped user:', mappedUser);
        setCurrentUserState(mappedUser);
        localStorage.setItem("currentUser", JSON.stringify(mappedUser)); // Update localStorage with mapped user
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem("currentUser");
      }
    }
    // Load initial data
    refreshData();
  }, []);

  // Load data based on role (using Firebase data)
  useEffect(() => {
    if (!currentUser) return;

    const loadRoleBasedData = async () => {
      if (currentUser.role === "super_admin") {
        await refreshData();
        setTraits(TRAITS);
        setQuestions(QUESTIONS);
        setSections(SECTIONS);
        setExams(EXAMS);
        setAssignments(ASSIGNMENTS);
        setResults(RESULTS);
      }

      if (currentUser.role === "org_admin") {
        const allOrgs = await fetchOrgs();
        setOrgs(allOrgs.filter(o => o.id === currentUser.orgId));

        const allUsers = await fetchUsers();
        setUsers(allUsers.filter(u => u.orgId === currentUser.orgId));

        setAssignments(ASSIGNMENTS.filter(a => a.orgId === currentUser.orgId));
        setResults(RESULTS.filter(r => r.orgId === currentUser.orgId));
        setExams(EXAMS.filter(e => e.orgId === currentUser.orgId));
        setSections(SECTIONS);
        setQuestions(QUESTIONS);
        setTraits(TRAITS);
      }

      if (currentUser.role === "candidate") {
        setAssignments(ASSIGNMENTS.filter(a => a.candidateId === currentUser.id));
        setResults(RESULTS.filter(r => r.candidateId === currentUser.id));
        setExams(EXAMS);
        setSections(SECTIONS);
        setQuestions(QUESTIONS);
        setTraits(TRAITS);
      }
    };

    loadRoleBasedData();
  }, [currentUser]);

  const setCurrentUser = (user: User | null) => {
    setCurrentUserState(user);
    if (user) {
      localStorage.setItem("currentUser", JSON.stringify(user));
    } else {
      localStorage.removeItem("currentUser");
      // Clear all data on logout
      setOrgs([]);
      setUsers([]);
      setTraits([]);
      setQuestions([]);
      setSections([]);
      setExams([]);
      setAssignments([]);
      setResults([]);
    }
  };

  // Firebase CRUD operations for Organizations
  const addOrg = async (org: Omit<Organization, 'id'>) => {
    try {
      const docRef = await addDoc(collection(db, "organizations"), {
        ...org,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        adminCount: org.adminCount || 0,
        candidateCount: org.candidateCount || 0,
      });
      const newOrg = { ...org, id: docRef.id };
      setOrgs(prev => [newOrg, ...prev]);
    } catch (error) {
      console.error("Error adding organization:", error);
      throw error;
    }
  };

  const updateOrg = async (id: string, data: Partial<Organization>) => {
    try {
      const orgRef = doc(db, "organizations", id);
      await updateDoc(orgRef, {
        ...data,
        updatedAt: Timestamp.now(),
      });
      setOrgs(prev => prev.map(org => org.id === id ? { ...org, ...data } : org));
    } catch (error) {
      console.error("Error updating organization:", error);
      throw error;
    }
  };

  const deleteOrg = async (id: string) => {
    try {
      // First, check if there are users in this organization
      const usersInOrg = users.filter(u => u.orgId === id);
      if (usersInOrg.length > 0) {
        console.warn(`Organization has ${usersInOrg.length} users. Consider handling them first.`);
      }

      await deleteDoc(doc(db, "organizations", id));
      setOrgs(prev => prev.filter(org => org.id !== id));

      // Optionally, update users that belonged to this org
      if (usersInOrg.length > 0) {
        const batch = writeBatch(db);
        usersInOrg.forEach(user => {
          const userRef = doc(db, "users", user.id);
          batch.update(userRef, { orgId: null });
        });
        await batch.commit();
        setUsers(prev => prev.map(user =>
          user.orgId === id ? { ...user, orgId: undefined } : user
        ));
      }
    } catch (error) {
      console.error("Error deleting organization:", error);
      throw error;
    }
  };

  // Firebase CRUD operations for Users
  const addUser = async (user: Omit<User, 'id'>): Promise<User> => {
    try {
      const docRef = await addDoc(collection(db, "users"), {
        ...user,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        hasPassword: false,
      });
      const newUser = { ...user, id: docRef.id, hasPassword: false };
      setUsers(prev => [newUser, ...prev]);

      // Update organization counts
      if (user.orgId) {
        const org = orgs.find(o => o.id === user.orgId);
        if (org) {
          const newAdminCount = user.role === "org_admin" ? (org.adminCount + 1) : org.adminCount;
          const newCandidateCount = user.role === "candidate" ? (org.candidateCount + 1) : org.candidateCount;
          await updateOrg(user.orgId, {
            adminCount: newAdminCount,
            candidateCount: newCandidateCount,
          });
        }
      }

      return newUser;
    } catch (error) {
      console.error("Error adding user:", error);
      throw error;
    }
  };

  const updateUser = async (id: string, data: Partial<User>) => {
    try {
      const userRef = doc(db, "users", id);
      const oldUser = users.find(u => u.id === id);

      await updateDoc(userRef, {
        ...data,
        updatedAt: Timestamp.now(),
      });
      setUsers(prev => prev.map(user => user.id === id ? { ...user, ...data } : user));

      // Update organization counts if role or orgId changed
      if (oldUser && (oldUser.role !== data.role || oldUser.orgId !== data.orgId)) {
        // Decrement old org counts
        if (oldUser.orgId) {
          const oldOrg = orgs.find(o => o.id === oldUser.orgId);
          if (oldOrg) {
            const newAdminCount = oldUser.role === "org_admin" ? Math.max(0, oldOrg.adminCount - 1) : oldOrg.adminCount;
            const newCandidateCount = oldUser.role === "candidate" ? Math.max(0, oldOrg.candidateCount - 1) : oldOrg.candidateCount;
            await updateOrg(oldUser.orgId, {
              adminCount: newAdminCount,
              candidateCount: newCandidateCount,
            });
          }
        }

        // Increment new org counts
        if (data.orgId) {
          const newOrg = orgs.find(o => o.id === data.orgId);
          if (newOrg) {
            const newAdminCount = data.role === "org_admin" ? (newOrg.adminCount + 1) : newOrg.adminCount;
            const newCandidateCount = data.role === "candidate" ? (newOrg.candidateCount + 1) : newOrg.candidateCount;
            await updateOrg(data.orgId, {
              adminCount: newAdminCount,
              candidateCount: newCandidateCount,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      const userToDelete = users.find(u => u.id === id);

      await deleteDoc(doc(db, "users", id));
      setUsers(prev => prev.filter(user => user.id !== id));

      // Update organization counts
      if (userToDelete?.orgId) {
        const org = orgs.find(o => o.id === userToDelete.orgId);
        if (org) {
          const newAdminCount = userToDelete.role === "org_admin" ? Math.max(0, org.adminCount - 1) : org.adminCount;
          const newCandidateCount = userToDelete.role === "candidate" ? Math.max(0, org.candidateCount - 1) : org.candidateCount;
          await updateOrg(userToDelete.orgId, {
            adminCount: newAdminCount,
            candidateCount: newCandidateCount,
          });
        }
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  return (
    <AppContext.Provider value={{
      currentUser,
      orgs,
      users,
      traits,
      questions,
      sections,
      exams,
      assessment,
      assignments,
      results,
      settings,
      loading,
      groups,
      setGroups,
      setCurrentUser,
      setOrgs,
      setUsers,
      setTraits,
      setQuestions,
      setSections,
      setExams,
      setAssignments,
      setResults,
      setSettings,
      addOrg,
      updateOrg,
      deleteOrg,
      addUser,
      updateUser,
      deleteUser,
      refreshData,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);